/**
 * Seeded random streams: the dice the starfield's sky and the seller
 * constellations roll, so the same seed always gives the same picture.
 *
 * In a module of its own, apart from the sky's model (src/lib/cosmos-model.ts,
 * which re-exports these), so code outside the sky can share them without
 * pulling the whole model into its bundle; src/lib/sky-bundle.test.ts holds
 * that line.
 */

export type Rng = () => number;

/** mulberry32: small, fast and plenty for scattering stars. Uniform in [0, 1). */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A uint32 seed for the stream named `tag` under `seed` (FNV-1a, then a murmur3 finaliser). */
export function deriveSeed(seed: number, tag: string): number {
  let h = (0x811c9dc5 ^ (seed >>> 0)) >>> 0;
  for (let i = 0; i < tag.length; i++) {
    h ^= tag.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** An independent stream: draws from one tag never shift another tag's sequence. */
export function deriveStream(seed: number, tag: string): Rng {
  return mulberry32(deriveSeed(seed, tag));
}
